import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from 'react';
import { useTheme } from '@mui/material';

const DAYS = [
  { key: 'Mo', label: 'Monday' },
  { key: 'Tu', label: 'Tuesday' },
  { key: 'We', label: 'Wednesday' },
  { key: 'Th', label: 'Thursday' },
  { key: 'Fr', label: 'Friday' },
  { key: 'Sa', label: 'Saturday' },
  { key: 'Su', label: 'Sunday' },
] as const;

const HOURS = Array.from({ length: 24 }, (_, i) => i);

interface TimeSlotData {
  day: string;
  timeRange: number[][];
}

interface TimeSlotSelectorProps {
  cellSize?: number;
  leftLabelWidth?: number;
  selectedColor?: string;
  setField: (field: string, value: any) => void;
  initialValue?: TimeSlotData[];
}

interface TimeSlotSummary {
  day: string;
  timeRange: number[][];
}

// Shade color utility (exact same as JS)
const shadeColor = (hex: string, percent: number) => {
  const f = hex.slice(1);
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  const R = parseInt(f.substring(0, 2), 16);
  const G = parseInt(f.substring(2, 4), 16);
  const B = parseInt(f.substring(4, 6), 16);
  return `rgb(${Math.round((t - R) * p + R)}, ${Math.round((t - G) * p + G)}, ${Math.round((t - B) * p + B)})`;
};

interface MarqueeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  cellSize = 28,
  leftLabelWidth = 56,
  selectedColor,
  setField,
  initialValue = [],
}) => {
  const theme = useTheme();
  const defaultSelectedColor = selectedColor || theme.palette.primary.main;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // refs and state for marquee selection
  const cellRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const cellRectsRef = useRef<Array<{ key: string; rect: DOMRect }>>([]);
  const dragPointerIdRef = useRef<number | null>(null);
  const dragStartRef = useRef<{
    x: number;
    y: number;
    key: string | null;
  } | null>(null);
  const dragModeRef = useRef<'add' | 'remove' | null>(null);
  const [hoverSelection, setHoverSelection] = useState<Set<string>>(new Set());
  const hoverSelectionRef = useRef<Set<string>>(new Set());
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);

  useEffect(() => {
    if (initialValue.length > 0) {
      const newSelected = new Set<string>();
      initialValue.forEach(({ day, timeRange }) => {
        const dayIndex = DAYS.findIndex(d => d.label === day);
        if (dayIndex !== -1) {
          timeRange.forEach(([start, end]) => {
            for (let h = start; h <= end; h++)
              newSelected.add(`${dayIndex}:${h}`);
          });
        }
      });
      setSelected(newSelected);
    }
  }, [initialValue]);

  // helpers
  const slotKey = useCallback(
    (dayIndex: number, hour: number) => `${dayIndex}:${hour}`,
    []
  );

  const computeCellRects = useCallback(() => {
    const items: Array<{ key: string; rect: DOMRect }> = [];
    cellRefs.current.forEach((el, key) => {
      if (el) items.push({ key, rect: el.getBoundingClientRect() });
    });
    cellRectsRef.current = items;
  }, []);

  const getNormalizedRect = (
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): MarqueeRect => {
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const right = Math.max(x1, x2);
    const bottom = Math.max(y1, y2);
    return { left, top, width: right - left, height: bottom - top };
  };

  const rectsIntersect = (a: MarqueeRect, b: DOMRect) => {
    const ax2 = a.left + a.width;
    const ay2 = a.top + a.height;
    const bx2 = b.left + b.width;
    const by2 = b.top + b.height;
    return !(ax2 < b.left || bx2 < a.left || ay2 < b.top || by2 < a.top);
  };

  const buildTimeSlotsData = useCallback(
    (sel: Set<string>) => {
      return DAYS.map((d, dayIndex) => {
        const hoursSelected = HOURS.filter(h => sel.has(slotKey(dayIndex, h)));
        if (!hoursSelected.length) return null;

        const sorted = hoursSelected.slice().sort((a, b) => a - b);
        const ranges: number[][] = [];
        let start = sorted[0],
          prev = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
          const cur = sorted[i];
          if (cur === prev + 1) prev = cur;
          else {
            ranges.push([start, prev]);
            start = prev = cur;
          }
        }
        ranges.push([start, prev]);
        return { day: d.label, timeRange: ranges };
      }).filter(Boolean) as TimeSlotSummary[];
    },
    [slotKey]
  );

  const updateSelected = useCallback(
    (updater: (prev: Set<string>) => Set<string>) => {
      setSelected(prev => {
        const s = updater(prev);
        setField('timeSlots', buildTimeSlotsData(s));
        return s;
      });
    },
    [setField, buildTimeSlotsData]
  );

  const toggleSingle = useCallback(
    (dayIndex: number, hour: number) => {
      updateSelected(prev => {
        const s = new Set(prev);
        const k = slotKey(dayIndex, hour);
        s.has(k) ? s.delete(k) : s.add(k);
        return s;
      });
    },
    [updateSelected, slotKey]
  );

  // Pointer-based marquee selection
  const startDrag = useCallback(
    (e: React.PointerEvent, startKey: string) => {
      e.preventDefault();
      // capture pointer events globally
      dragPointerIdRef.current = e.pointerId;
      dragStartRef.current = { x: e.clientX, y: e.clientY, key: startKey };
      const isSelected = selected.has(startKey);
      dragModeRef.current = isSelected ? 'remove' : 'add';
      document.body.style.userSelect = 'none';
      computeCellRects();
      const initial = new Set([startKey]);
      hoverSelectionRef.current = initial;
      setHoverSelection(initial);
      setMarquee({ left: e.clientX, top: e.clientY, width: 0, height: 0 });

      const handleMove = (ev: PointerEvent) => {
        if (!dragStartRef.current) return;
        const { x, y } = dragStartRef.current;
        const m = getNormalizedRect(x, y, ev.clientX, ev.clientY);
        setMarquee(m);
        const keys: string[] = [];
        for (const { key, rect } of cellRectsRef.current) {
          if (rectsIntersect(m, rect)) keys.push(key);
        }
        const next = new Set(keys);
        hoverSelectionRef.current = next;
        setHoverSelection(next);
      };

      const handleUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        document.body.style.userSelect = '';
        const start = dragStartRef.current;
        const mode = dragModeRef.current;
        dragStartRef.current = null;
        dragModeRef.current = null;
        dragPointerIdRef.current = null;

        const dx = ev.clientX - (start?.x ?? ev.clientX);
        const dy = ev.clientY - (start?.y ?? ev.clientY);
        const moved = Math.hypot(dx, dy) > 3;

        if (!start || !mode) {
          setHoverSelection(new Set());
          setMarquee(null);
          return;
        }

        if (!moved) {
          // treat as single click
          const [dStr, hStr] = start.key!.split(':');
          toggleSingle(Number(dStr), Number(hStr));
          setHoverSelection(new Set());
          setMarquee(null);
          return;
        }

        const keysToApply = hoverSelectionRef.current;
        if (keysToApply.size === 0) {
          setHoverSelection(new Set());
          setMarquee(null);
          return;
        }
        updateSelected(prev => {
          const s = new Set(prev);
          for (const k of keysToApply) {
            if (mode === 'add') s.add(k);
            else s.delete(k);
          }
          return s;
        });
        setHoverSelection(new Set());
        setMarquee(null);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [computeCellRects, selected, toggleSingle, updateSelected]
  );

  const summary = useMemo(
    () => buildTimeSlotsData(selected),
    [selected, buildTimeSlotsData]
  );
  const gridTemplateColumns = `${leftLabelWidth}px repeat(24, ${cellSize}px) auto`;

  const styles: Record<string, React.CSSProperties> = {
    wrapper: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: 16,
      maxWidth: '100%',
      overflowX: 'auto',
      background: theme.palette.background.paper,
      fontFamily: theme.typography.fontFamily,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      margin: 0,
      marginBottom: 12,
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.h6.fontWeight,
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
    },
    clearBtn: {
      marginLeft: 12,
      padding: '6px 10px',
      borderRadius: 6,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.mode === 'dark' ? '#1d1d1d' : '#fff',
      color: theme.palette.text.primary,
      cursor: 'pointer',
      fontSize: theme.typography.body2.fontSize,
    },
    gridHeader: {
      display: 'grid',
      gridTemplateColumns,
      alignItems: 'center',
      gap: 6,
      marginBottom: 6,
    },
    hourCell: {
      width: cellSize,
      height: cellSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.caption.fontSize,
      color: theme.palette.text.secondary,
      userSelect: 'none',
      fontFamily: theme.typography.fontFamily,
    },
    dayRow: {
      display: 'grid',
      gridTemplateColumns,
      alignItems: 'center',
      gap: 6,
      marginBottom: 6,
    },
    dayLabel: {
      width: leftLabelWidth,
      minWidth: leftLabelWidth,
      textAlign: 'center',
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.primary,
      userSelect: 'none',
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.body2.fontWeight,
    },
    slot: {
      width: cellSize,
      height: cellSize,
      boxSizing: 'border-box',
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.action.hover,
      cursor: 'crosshair',
      userSelect: 'none',
      transition: theme.transitions.create(
        ['background-color', 'border-color'],
        {
          duration: theme.transitions.duration.short,
        }
      ),
    },
    summaryCell: {
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      paddingLeft: 8,
      fontFamily: theme.typography.fontFamily,
    },
    marquee: {
      position: 'fixed',
      zIndex: 9999,
      pointerEvents: 'none',
      border: `1px dashed ${shadeColor(defaultSelectedColor, -10)}`,
      backgroundColor: shadeColor(defaultSelectedColor, 60)
        .replace('rgb', 'rgba')
        .replace(')', ', 0.15)'),
    } as React.CSSProperties,
  };

  const clearAll = useCallback(() => {
    setSelected(new Set());
    setField('timeSlots', []);
  }, [setField]);

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.toolbar, marginBottom: 12 }}>
        <h3 style={styles.title}>Custom Dayparting</h3>
        <button
          type="button"
          onClick={clearAll}
          style={styles.clearBtn}
          title="Clear all selection"
        >
          Clear
        </button>
      </div>

      <div style={styles.gridHeader}>
        <div style={{ width: leftLabelWidth }} />
        {HOURS.map(h => (
          <div key={h} style={styles.hourCell}>
            {h}
          </div>
        ))}
        <div style={{ fontWeight: 600, fontSize: 13, paddingLeft: 8 }}>
          Summary
        </div>
      </div>

      {DAYS.map((d, dayIndex) => {
        const ranges = summary.find(s => s.day === d.label)?.timeRange || [];
        return (
          <div key={d.key} style={styles.dayRow}>
            <div style={styles.dayLabel}>{d.key}</div>
            {HOURS.map(h => {
              const k = slotKey(dayIndex, h);
              const isSelected = selected.has(k);
              return (
                <div
                  key={k}
                  ref={el => {
                    if (el) cellRefs.current.set(k, el);
                    else cellRefs.current.delete(k);
                  }}
                  onPointerDown={e => startDrag(e, k)}
                  style={{
                    ...styles.slot,
                    background:
                      isSelected || hoverSelection.has(k)
                        ? defaultSelectedColor
                        : styles.slot.background,
                    border:
                      isSelected || hoverSelection.has(k)
                        ? `1px solid ${shadeColor(defaultSelectedColor, -10)}`
                        : styles.slot.border,
                  }}
                  title={`${d.label} ${h}:00`}
                />
              );
            })}
            <div style={styles.summaryCell}>
              {ranges.length > 0
                ? ranges
                    .map(([s, e]) => (s === e ? `${s}` : `${s}-${e}`))
                    .join(', ')
                : '-'}
            </div>
          </div>
        );
      })}
      {marquee && marquee.width > 0 && marquee.height > 0 && (
        <div
          style={{
            ...styles.marquee,
            left: marquee.left,
            top: marquee.top,
            width: marquee.width,
            height: marquee.height,
          }}
        />
      )}
    </div>
  );
};

export default memo(TimeSlotSelector);
